import { useState, useRef, useEffect } from "react";
import { theme } from "../theme.js";

export default function FilterDropdown({ 
  options = [], 
  selectedFilters = [], 
  onFiltersChange, 
  label = "Filter", 
  onOpen, 
  shouldClose, 
  filterType = "select", // "select" | "distance" | "date"
  minValue, 
  maxValue, 
  onIntervalChange, // unified callback for both distance and date
  align = "left" 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState(selectedFilters || []);
  const [localMinValue, setLocalMinValue] = useState(minValue || "");
  const [localMaxValue, setLocalMaxValue] = useState(maxValue || "");
  const dropdownRef = useRef(null);

  // Close dropdown when shouldClose becomes true
  useEffect(() => {
    if (shouldClose && isOpen) {
      setIsOpen(false);
    }
  }, [shouldClose]);

  const handleToggleOption = (option) => {
    if (localFilters.includes(option)) {
      setLocalFilters(localFilters.filter(f => f !== option));
    } else {
      setLocalFilters([...localFilters, option]);
    }
  };

  const handleDone = () => {
    if (filterType === "distance" || filterType === "date") {
      if (onIntervalChange) {
        const min = filterType === "date" && localMinValue ? new Date(localMinValue) : localMinValue;
        const max = filterType === "date" && localMaxValue ? new Date(localMaxValue) : localMaxValue;
        onIntervalChange(min, max);
      }
    } else {
      onFiltersChange(localFilters);
    }
    setIsOpen(false);
  };

  const handleReset = () => {
    if (filterType === "distance" || filterType === "date") {
      setLocalMinValue("");
      setLocalMaxValue("");
    } else {
      setLocalFilters([]);
    }
  };

  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedFilters]);

  return (
    <div style={styles.filterDropdown} ref={dropdownRef}>
      <button
        onClick={() => {
          const newState = !isOpen;
          setIsOpen(newState);
          if (newState && onOpen) {
            onOpen();
          }
        }}
        style={{
          ...styles.filterButton,
          ...(isOpen ? styles.filterButtonActive : {}),
        }}
      >
        <span>{label}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          style={{
            ...styles.chevronIcon,
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: `transform ${theme.transitions.base}`,
          }}
        >
          <path
            d="M7 10l5 5 5-5z"
            fill="currentColor"
          />
        </svg>
      </button>

      {isOpen && (
        <div style={{
          ...styles.dropdownMenu,
          ...(align === "right"  ? { left: "auto", right: 0 } : {}),
          ...(align === "center" ? { left: "50%", transform: "translateX(-50%)" } : {}),
        }}>
          {selectedFilters.length > 0 && (
            <div style={styles.dropdownHeader}>
              <button
                onClick={() => {
                  onFiltersChange([]);
                  setLocalFilters([]);
                  setIsOpen(false);
                }}
                style={styles.clearButton}
                title="Clear all filters"
              >
                ✕
              </button>
            </div>
          )}
          <div style={styles.optionsContainer}>
            {(filterType === "distance" || filterType === "date") ? (
              <div style={styles.intervalInputsWrapper}>
                <div style={styles.intervalInput}>
                  <label style={styles.intervalLabel}>
                    {filterType === "date" ? "From:" : "Min (km)"}
                  </label>
                  <input
                    type={filterType === "date" ? "date" : "number"}
                    min="0"
                    placeholder={filterType === "date" ? "" : "0"}
                    value={localMinValue}
                    onChange={(e) => setLocalMinValue(e.target.value)}
                    style={styles.intervalInputField}
                  />
                </div>
                <div style={styles.intervalInput}>
                  <label style={styles.intervalLabel}>
                    {filterType === "date" ? "To:" : "Max (km)"}
                  </label>
                  <input
                    type={filterType === "date" ? "date" : "number"}
                    min="0"
                    placeholder={filterType === "date" ? "" : "∞"}
                    value={localMaxValue}
                    onChange={(e) => setLocalMaxValue(e.target.value)}
                    style={styles.intervalInputField}
                  />
                </div>
              </div>
            ) : (
              options.map((option) => {
                // Handle both string and object options
                const optionId = typeof option === 'object' ? option.id : option;
                const optionLabel = typeof option === 'object' ? option.name : option;
                
                return (
                  <label key={optionId} style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={localFilters.includes(optionId)}
                      onChange={() => handleToggleOption(optionId)}
                      style={styles.checkbox}
                    />
                    <span style={styles.optionText}>
                      {optionLabel.charAt(0).toUpperCase() + optionLabel.slice(1)}
                    </span>
                  </label>
                );
              })
            )}
          </div>
          

          {/* Actions */}
          <div style={styles.dropdownActions}>
            <button
              onClick={handleReset}
              style={styles.resetButton}
            >
              Reset
            </button>
            <button
              onClick={handleDone}
              style={styles.doneButton}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  filterDropdown: {
    position: "relative",
    display: "inline-block",
  },
  filterButton: {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    color: theme.colors.white,
    border: `2px solid rgba(255, 255, 255, 0.3)`,
    borderRadius: theme.radius.full,
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.medium,
    cursor: "pointer",
    transition: `all ${theme.transitions.base}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    whiteSpace: "nowrap",
  },
  filterButtonActive: {
    backgroundColor: theme.colors.light,
    color: theme.colors.dark,
    border: `2px solid ${theme.colors.light}`,
  },
  chevronIcon: {
    display: "flex",
    alignItems: "center",
  },
  dropdownMenu: {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: 0,
    backgroundColor: theme.colors.white,
    borderRadius: theme.radius.lg,
    boxShadow: theme.shadows.lg,
    zIndex: theme.zIndex.dropdown,
    overflow: "hidden",
  },
  optionsContainer: {
    padding: theme.spacing.sm,
    maxHeight: "300px",
    overflowY: "auto",
  },
  dropdownHeader: {
    display: "flex",
    justifyContent: "flex-end",
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  clearButton: {
    backgroundColor: "transparent",
    color: theme.colors.textMuted,
    border: "none",
    cursor: "pointer",
    fontSize: theme.fonts.size.lg,
    padding: 0,
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: `all ${theme.transitions.base}`,
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    cursor: "pointer",
    transition: `background ${theme.transitions.base}`,
    borderRadius: theme.radius.md,
    userSelect: "none",
    gap: theme.spacing.sm,
  },
  checkbox: {
    cursor: "pointer",
    width: "18px",
    height: "18px",
    accentColor: theme.colors.light,
  },
  optionText: {
    fontSize: theme.fonts.size.base,
    color: theme.colors.textInverted,
    fontWeight: theme.fonts.weight.medium,
  },
  dropdownActions: {
    display: "flex",
    gap: theme.spacing.md,
    padding: `${theme.spacing.md} ${theme.spacing.md}`,
    backgroundColor: theme.colors.bgLight,
    borderTop: `1px solid ${theme.colors.gray400}`,
    justifyContent: "space-between",
  },
  resetButton: {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    backgroundColor: "transparent",
    color: theme.colors.dark,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.md,
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.medium,
    cursor: "pointer",
    transition: `all ${theme.transitions.base}`,
  },
  doneButton: {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    backgroundColor: theme.colors.light,
    color: theme.colors.textInverted,
    border: "none",
    borderRadius: theme.radius.md,
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.medium,
    cursor: "pointer",
    transition: `all ${theme.transitions.base}`,
  },
  intervalInputsWrapper: {
    display: "flex",
    gap: theme.spacing.md,
    padding: `${theme.spacing.md}`,
    alignItems: "flex-end",
    flexWrap: "wrap",
  },
  intervalInput: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xs,
    flex: "0 1 auto",
    maxWidth: "150px",
  },
  intervalLabel: {
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.medium,
    color: theme.colors.textInverted,
    textAlign: "left",
  },
  intervalInputField: {
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.border}`,
    fontSize: theme.fonts.size.sm,
    outline: "none",
    accentColor: theme.colors.light,
  },
  dateInputsWrapper: {
    display: "flex",
    gap: theme.spacing.md,
    padding: `${theme.spacing.md}`,
    alignItems: "flex-end",
    flexWrap: "wrap",
  },
  dateInput: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xs,
    flex: 1,
  },
  dateLabel: {
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.medium,
    color: theme.colors.textInverted,
  },
  dateInputField: {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.border}`,
    fontSize: theme.fonts.size.sm,
    outline: "none",
    accentColor: theme.colors.light,
  },
};
